import * as React from "react";

import { AccessReportsCoverPageElementEnum, AccessReportsWizardElement, AccessReportsWizardFormGroup, AccessReportsWizardGroupPropertyIdEnum, AccessReportsWizardPage, AccessReportsWizardStepEnum, AccessReportsWizardTemplate } from "@app/access-reports/shared";
import { getElementByIdFromTemplate, getPageFromTemplate } from "@app/access-reports/shared/utility";
import { QESButton, QESCheckbox, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { cloneDeep } from "lodash";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";

interface AddFormGroupSidePanelProps {
  onAddNodeClick: (template: AccessReportsWizardTemplate) => void;
  page: AccessReportsWizardStepEnum;
  template: AccessReportsWizardTemplate;
}

export const AddFormGroupSidePanel: React.FC<React.PropsWithChildren<AddFormGroupSidePanelProps>> = (props: AddFormGroupSidePanelProps) => {
  const [formGroup, setFormGroup] = React.useState<AccessReportsWizardFormGroup>(null);
  const [isAccessPage, setIsAccessPage] = React.useState<boolean>(false);
  const [isTitleValid, setIsTitleValid] = React.useState<boolean>(true);

  React.useEffect(() => {
    const newFormGroup: AccessReportsWizardFormGroup = {
      byProductDefaults: [],
      disabled: false,
      disallowedMultipleProducts: [],
      elements: [],
      groupPropertyId: null,
      hidden: false,
      hiddenProducts: [],
      id: getMaxFormGroupId(),
      title: "",
    };

    setFormGroup(newFormGroup);
  }, []);

  const getAddDisabledStatus = (): boolean => {
    if (isAccessPage) {
      if (!!formGroup?.groupPropertyId) {
        return false;
      }
    } else {
      if (isTitleValid) {
        return false;
      }
    }

    return true;
  }

  const getMaxFormGroupId = (): number => {
    const page: AccessReportsWizardPage = getPageFromTemplate(props.template, props.page);
    let maxId = 0;

    if (page?.formGroups.length) {
      maxId = Math.max(...page.formGroups.map((x) => x.id)) + 1;
    }

    return maxId;
  }

  const groupPropertyIds: AccessReportsWizardGroupPropertyIdEnum[] = React.useMemo(() => {
    // only allow the user the ability to select a group property id that is not already present in the template,
    // seeing as access pages should only have one node of each type in the template
    let groupProperties: AccessReportsWizardGroupPropertyIdEnum[] = Object.values(AccessReportsWizardGroupPropertyIdEnum);
    const pageOptionsPage: AccessReportsWizardPage = getPageFromTemplate(props.template, AccessReportsWizardStepEnum.PageOptions);

    for (const groupProperty of groupProperties) {
      if (!!pageOptionsPage.formGroups.find(x => x.groupPropertyId === groupProperty)) {
        groupProperties = groupProperties.filter(x => x !== groupProperty);
      }
    }

    return groupProperties;
  }, []);

  const handleHideCheckboxChange = (checked: boolean): void => {
    setFormGroup({
      ...formGroup,
      hidden: checked,
    });
  }

  const handleHideProductsCheckboxChange = (checked: boolean): void => {
    setFormGroup({
      ...formGroup,
      hiddenProducts: checked ? productIds : [],
    });
  }

  const handleDisableCheckboxChange = (checked: boolean): void => {
    setFormGroup({
      ...formGroup,
      disabled: checked,
    });
  }

  const handleDisallowMultiplesCheckboxChange = (checked: boolean): void => {
    setFormGroup({
      ...formGroup,
      disallowedMultipleProducts: checked ? productIds : [],
    });
  }

  const handleIsAccessPageCheckboxChange = (checked: boolean): void => {
    // access pages do not need titles
    setFormGroup({
      ...formGroup,
      title: "",
    });

    setIsAccessPage(checked);
  }

  const handleGroupPropertyChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setFormGroup({
      ...formGroup,
      groupPropertyId: event.target.value as AccessReportsWizardGroupPropertyIdEnum,
    });
  }

  const getBody = (): JSX.Element => {
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-md-4">
            <label className="label-s mb-0">
              Title
            </label>
          </div>

          <div className="col-md-8">
            <QESTextInput
              disabled={isAccessPage}
              testId="access-reports-admin__template-update--add-form-group-title"
              errorText="A form group with that name within this page already exists"
              hideLabel={true}
              isValid={isTitleValid}
              label="Title"
              onChange={(e) => onTitleChange(e)}
              unrequiredValidation={true}
              value={formGroup.title}
            />
          </div>
        </div>

        <div className="row">
          <div className="offset-md-4 col-md-8">
            <QESCheckbox
              checked={formGroup.hidden}
              label="Hide"
              testId="access-reports-admin__template-update--add-form-group-hidden"
              onChange={(e) => handleHideCheckboxChange(e.target.checked)}
            />
          </div>
        </div>

        <div className="row">
          <div className="offset-md-4 col-md-8">
            <QESCheckbox
              checked={formGroup.disabled}
              label="Disable"
              testId="access-reports-admin__template-update--add-form-group-disabled"
              onChange={(e) => handleDisableCheckboxChange(e.target.checked)}
            />
          </div>
        </div>

        {groupPropertyIds.length > 0 && (
          <React.Fragment>
            <div className="row">
              <div className="offset-md-4 col-md-8">
                <QESCheckbox
                  checked={isAccessPage}
                  label="Is Access Page"
                  testId="access-reports-admin__template-update--add-form-group-is-access"
                  onChange={(e) => handleIsAccessPageCheckboxChange(e.target.checked)}
                />
              </div>
            </div>

            {isAccessPage && (
              <React.Fragment>
                <div className="row">
                  <div className="col-md-4">
                    <label className="label-s mb-0">
                      Access Page Type
                    </label>
                  </div>

                  <div className="col-md-8">
                    <QESSelect
                      allowEmpty={true}
                      testId="access-reports-admin__template-update--add-form-group-group-properties"
                      label=""
                      onChange={(e) => handleGroupPropertyChange(e)}
                      value={formGroup.groupPropertyId || ""}
                    >
                      {groupPropertyIds.map((option, optionIndex) => (
                        <option
                          key={optionIndex}
                          value={option}
                        >
                          {option}
                        </option>
                      ))}
                    </QESSelect>
                  </div>
                </div>

                <div className="row">
                  <div className="offset-md-4 col-md-8">
                    <QESCheckbox
                      checked={formGroup.hiddenProducts.length > 0}
                      label="Hide For All Products"
                      testId="access-reports-admin__template-update--add-form-group-hide-products"
                      onChange={(e) => handleHideProductsCheckboxChange(e.target.checked)}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="offset-md-4 col-md-8">
                    <QESCheckbox
                      checked={formGroup.disallowedMultipleProducts.length > 0}
                      label="Disallow Multiples For All Products"
                      testId="access-reports-admin__template-update--add-form-group-disallow-products"
                      onChange={(e) => handleDisallowMultiplesCheckboxChange(e.target.checked)}
                    />
                  </div>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const page: AccessReportsWizardPage = getPageFromTemplate(props.template, props.page);

    // form group title is invalid if a form group with that title already exists on the page
    if (page.formGroups.map(x => x.title).includes(event.target.value)) {
      setIsTitleValid(false);
    } else {
      setIsTitleValid(true);
    }

    setFormGroup({
      ...formGroup,
      title: event.target.value,
    });
  }

  const onAddFormGroup = () => {
    const template: AccessReportsWizardTemplate = cloneDeep(props.template);
    const page: AccessReportsWizardPage = getPageFromTemplate(template, props.page);
    page.formGroups.push(formGroup);

    props.onAddNodeClick(template);
  }

  const productIds: number[] = React.useMemo(() => {
    const productElement: AccessReportsWizardElement = getElementByIdFromTemplate(props.template, AccessReportsWizardStepEnum.CoverPage, AccessReportsCoverPageElementEnum.Product);
    return productElement?.options.map(x => x.id);
  }, []);

  return (
    <React.Fragment>
      {!!formGroup && (
        getBody()
      )}

      <FlyoutFooter
        submitButton={
          <QESButton
            className="ml-3"
            disabled={getAddDisabledStatus()}
            onClick={onAddFormGroup}
            qesTestId="access-reports-admin__template-update--add-form-group-add"
            qesType="primary"
          >
            Add
          </QESButton>
        }
      />
    </React.Fragment>
  );
}
